import { inject, Injectable } from '@angular/core';
import { from, tap } from 'rxjs';

import {
  getMutations,
  getResourceCollection
} from '@/core/utils/async-resources';
import { BaseRepository } from '@/core/services/base-repository';
import { ApiService } from '@/core/api.service';
import {
  Beneficiary,
  BeneficiaryRequestCreate,
  BeneficiaryRequestDelete,
  BeneficiaryRequestUpdate,
  BeneficiarySearchParams
} from '../models/connection';
import { SessionService } from '@/core/session.service';

/**
 * Repository that encapsulates all data access required by the beneficiary entity feature.
 *
 * @remarks
 * The class uses the shared async repository helpers to expose signals that
 * components can bind to without manually handling HTTP state.
 */
@Injectable({
  providedIn: 'root'
})
export class BeneficiaryRepository extends BaseRepository {
  private readonly apiService = inject(ApiService);
  private readonly sessionService = inject(SessionService);

  constructor() {
    super();
  }

  /**
   * @description Define las mutaciones CRUD para beneficiarios
   * @returns {ReturnType<typeof getMutations>} Objeto con create, update y delete
   */
  public mutations() {
    return getMutations({
      create: (data: BeneficiaryRequestCreate) =>
        from(
          this.apiService.call({
            microServiceName: 'WSMPBeneficiaryManagement',
            resourcePath: '/beneficiary/register-beneficiary',
            method: 'POST',
            useToken: true,
            body: data,
            bodyEncrypted: true,
            responseEncrypted: true
          })
        ).pipe(
          tap(() =>
            this.notifyChange(
              'create',
              data.beneficiary.sequence != null
                ? [String(data.beneficiary.sequence)]
                : []
            )
          )
        ),
      update: (data: BeneficiaryRequestUpdate) =>
        from(
          this.apiService.call({
            microServiceName: 'WSMPBeneficiaryManagement',
            resourcePath: '/beneficiary/edit-beneficiary',
            method: 'PUT',
            useToken: true,
            body: this.mapData(data),
            bodyEncrypted: true
          })
        ).pipe(
          tap(() =>
            this.notifyChange('update', [String(data.beneficiary.sequence)])
          )
        ),
      delete: (data: BeneficiaryRequestDelete) =>
        from(
          this.apiService.call({
            microServiceName: 'WSMPBeneficiaryManagement',
            resourcePath: `/beneficiary/delete-beneficiary/{lenderId}/N/${data.sequence}/{email}`,
            method: 'DELETE',
            useToken: true,
            pathParams: {
              lenderId: {
                value: this.sessionService.settings()?.lenderId,
                encrypted: true
              },
              email: {
                value: data.email,
                encrypted: true,
              }
            }
          })
        ).pipe(tap(() => this.notifyChange('delete', [String(data.sequence)])))
    });
  }

  /**
   * @description Obtiene la colección de beneficiarios con búsqueda paginada
   * @returns {ReturnType<typeof getResourceCollection>} Colección reactiva de beneficiarios
   */
  public findBy() {
    return getResourceCollection<BeneficiarySearchParams, Beneficiary[]>(
      async (searchParams: BeneficiarySearchParams) => {
        const response = await this.apiService.call({
          microServiceName: 'WSMPBeneficiaryManagement',
          resourcePath: `/beneficiary/get-beneficiaries-by-lenderId/{lenderId}/${searchParams.pageLimit}/${searchParams.pageNumber}`,
          method: 'GET',
          useToken: true,
          pathParams: {
            lenderId: {
              value:
                searchParams.lenderId ??
                this.sessionService.settings()?.lenderId,
              encrypted: true
            }
          },
          responseEncrypted: true
        });
        const data = (response.data.map((item: any) => {
          item.sequence = item.sequential;
          delete item.sequential;
          return item;
        }) ?? []) as Beneficiary[];
        return data;
      }
    );
  }

  /**
   * @description Mapea los datos del beneficiario para la API (sequence → sequential)
   * @param {any} data - Datos a mapear
   * @returns {Beneficiary[]} Datos formateados para la API
   * @private
   */
  private mapData(data: any): Beneficiary[] {
    data.beneficiary.sequential = data.beneficiary.sequence;
    delete data.beneficiary.sequence;
    return data;
  }
}
